package components.auth

import androidx.compose.runtime.*
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import design.DesignTokens

/**
 * Password Strength Levels
 */
enum class PasswordStrength(val label: String, val color: String, val percent: Int) {
    WEAK("弱い", "#ff4757", 25),
    FAIR("やや弱い", "#ffa502", 50),
    GOOD("普通", "#eccc68", 75),
    STRONG("強い", "#2ed573", 100);

    companion object {
        fun fromScore(score: Int): PasswordStrength {
            return when {
                score <= 1 -> WEAK
                score <= 2 -> FAIR
                score <= 3 -> GOOD
                else -> STRONG
            }
        }
    }
}

/**
 * Calculate password strength score (0-4)
 * Based on: length, lowercase, uppercase, numbers, special chars
 */
fun calculatePasswordStrength(password: String): Int {
    if (password.isEmpty()) return 0

    var score = 0

    // Length check
    if (password.length >= 8) score++
    if (password.length >= 12) score++

    // Character variety
    if (password.any { it.isLowerCase() }) score++
    if (password.any { it.isUpperCase() }) score++
    if (password.any { it.isDigit() }) score++
    if (password.any { !it.isLetterOrDigit() }) score++

    return score.coerceAtMost(4)
}

/**
 * Get password requirements text
 */
fun getPasswordRequirements(password: String): List<Pair<String, Boolean>> {
    return listOf(
        "8文字以上" to (password.length >= 8),
        "大文字と小文字を含む" to (password.any { it.isUpperCase() } && password.any { it.isLowerCase() }),
        "数字を含む" to password.any { it.isDigit() },
        "特殊文字を含む" to password.any { !it.isLetterOrDigit() }
    )
}

/**
 * Password Strength Meter Component
 *
 * Displays a visual indicator of password strength with:
 * - Progress bar with color coding
 * - Text label (弱い/やや弱い/普通/強い)
 * - Requirements checklist
 *
 * @param password Current password value
 * @param showRequirements Whether to show detailed requirements list
 */
@Composable
fun PasswordStrengthMeter(
    password: String,
    showRequirements: Boolean = true
) {
    val strength = remember(password.length) {
        PasswordStrength.fromScore(calculatePasswordStrength(password))
    }

    val requirements = remember(password) {
        getPasswordRequirements(password)
    }

    Div({
        style {
            property("margin-top", "12px")
        }
    }) {
        // Strength bar container
        Div({
            style {
                property("display", "flex")
                property("align-items", "center")
                property("gap", "12px")
                property("margin-bottom", if (showRequirements) "12px" else "0")
            }
        }) {
            // Progress bar
            Div({
                style {
                    property("flex", "1")
                    property("height", "8px")
                    property("background-color", "#e0e0e0")
                    property("border-radius", "4px")
                    property("overflow", "hidden")
                    property("position", "relative")
                }
            }) {
                Div({
                    style {
                        property("height", "100%")
                        property("width", if (password.isEmpty()) "0%" else "${strength.percent}%")
                        property("background-color", strength.color)
                        property("transition", "width 0.3s ease, background-color 0.3s ease")
                    }
                    attr("aria-label", "パスワード強度: ${strength.label}")
                    attr("role", "progressbar")
                    attr("aria-valuenow", if (password.isEmpty()) "0" else "${strength.percent}")
                    attr("aria-valuemin", "0")
                    attr("aria-valuemax", "100")
                }) {}
            }

            // Strength label
            Span({
                style {
                    property("font-size", "14px")
                    property("font-weight", "500")
                    property("color", if (password.isEmpty()) "#999" else strength.color)
                    property("min-width", "50px")
                }
            }) {
                Text(if (password.isEmpty()) "-" else strength.label)
            }
        }

        // Requirements list
        if (showRequirements && password.isNotEmpty()) {
            Div({
                style {
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("gap", "6px")
                    property("font-size", "13px")
                }
            }) {
                requirements.forEach { (requirement, met) ->
                    Div({
                        style {
                            property("display", "flex")
                            property("align-items", "center")
                            property("gap", "8px")
                            property("color", if (met) DesignTokens.Colors.success else DesignTokens.Colors.textSecondary)
                        }
                    }) {
                        // Icon
                        Span({
                            style {
                                property("font-size", "16px")
                            }
                        }) {
                            Text(if (met) "✓" else "○")
                        }
                        // Requirement text
                        Text(requirement)
                    }
                }
            }
        }
    }
}

/**
 * Compact Password Strength Indicator
 *
 * Minimal version with just a colored indicator dot
 *
 * @param password Current password value
 */
@Composable
fun PasswordStrengthIndicator(
    password: String
) {
    val strength = remember(password.length) {
        PasswordStrength.fromScore(calculatePasswordStrength(password))
    }

    Div({
        style {
            property("display", "inline-flex")
            property("align-items", "center")
            property("gap", "6px")
            property("font-size", "12px")
        }
        attr("aria-label", "パスワード強度: ${strength.label}")
    }) {
        // Indicator dot
        Span({
            style {
                property("width", "8px")
                property("height", "8px")
                property("border-radius", "50%")
                property("background-color", if (password.isEmpty()) "#e0e0e0" else strength.color)
            }
        }) {}
        // Label
        if (password.isNotEmpty()) {
            Span({
                style {
                    property("color", strength.color)
                }
            }) {
                Text(strength.label)
            }
        }
    }
}
